import { edgeHostedEntityGetLayerEdgeLength } from "building-generation/elements/edgePlacementUtils";
import type { Building } from "building-types/building/buildingTypes";
import type { BlindPanelSide } from "building-types/casework/caseworkTypesEnum";
import type {
  WallCasework,
  WallCaseworkFixed,
  WallCaseworkFixedDeprecated,
} from "building-types/casework/wallCaseworkTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { CaseworkDoorStyle } from "building-types/settings/settingsTypesEnum";
import { isEdgeHostedEntityFromEnd } from "doc/elements/openings/openingWallLayersUtils";
import { isDeprecatedCasework, isFixedCasework } from "doc/utils/caseworkDescriptorUtils";
import { arc2ByCenterStartAngle } from "geom/arc";
import type { BRep } from "geom/brep";
import { faceNew, type Extrusion } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import { extrudeFaceAlongNormal, extrudeFaceAlongPolyLine3, extrudePolyLine2AlongNormal } from "geom/extrude";
import { line2Eval, line2Len } from "geom/line2";
import { offsetPolyLine2, polyLine2Thicken } from "geom/offset";
import { planeNewXY, polyLine2OnPlane } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2ByTwoPoints,
  polyLine2Concat,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeEval,
  polyLine2EdgeLength,
  polyLine2End,
  polyLine2Extend,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2IsClosed,
  polyLine2Length,
  polyLine2MergeCoincidentVertices,
  polyLine2Move,
  polyLine2Reverse,
  polyLine2ReverseCopy,
  polyLine2SetCW,
  polyLine2Start,
} from "geom/polyLine2";
import type { LineAnchor, PolyLine2 } from "geom/polyLine2Types";
import { rangeInclusiveByMinStep } from "geom/range";
import { vec2Normalize, vec2Perp } from "geom/vec2";
import {
  cornerFeatureAtPathStart,
  miterBackPath,
  miterFrontPath,
  notchFrontPath,
  offsetCabinetFront,
  type BackCornerMiter,
  type FrontCornerMiter,
  type FrontCornerNotch,
} from "./caseworkShape";

export const HALF_CABINET_GAP = 1;
/** Bare face left at each end of an angled cabinet's front, so its doors stop short of the edges. */
const ANGLED_DOOR_END_REVEAL = 2;
/** The doors already sit `HALF_CABINET_GAP` in from their path's ends, so the path gives up the rest. */
const ANGLED_DOOR_PATH_INSET = ANGLED_DOOR_END_REVEAL - HALF_CABINET_GAP;
const CABINET_SPACING = 24.5;
const CABINET_DOOR_THICKNESS = 0.75;
const CABINET_SHAKER_SLAT_WIDTH = 2.75;
const HANDLE_THICKESS = 0.25;
const HANDLE_HALF_WIDTH = 1.5;
interface CabinetAlongPathOptions {
  stripPath: PolyLine2;
  finalBaseHeight: number;
  thickness: number; //is overall casework height
  depth: number;
  lateralOffset: number;
  paramStart?: number;
  paramStartNormalized?: boolean;
  paramEnd?: number;
  paramEndNormalized?: boolean;
  countertopThickness: number;
  countertopOverhang: number;
  toeKickHeight: number;
  toeKickDepth: number;
  paramAnchor: LineAnchor;
  mirror: boolean;
  isDeprecatedPlacement: boolean;
  frontCornerMiter?: FrontCornerMiter;
  frontCornerNotch?: FrontCornerNotch;
  backCornerMiter?: BackCornerMiter;
}

export function cabinetAlongPath({
  stripPath,
  finalBaseHeight,
  thickness,
  depth,
  lateralOffset,
  paramStart,
  paramStartNormalized,
  paramEnd,
  paramEndNormalized,
  countertopThickness,
  countertopOverhang,
  toeKickHeight,
  toeKickDepth,
  paramAnchor,
  mirror,
  isDeprecatedPlacement,
  frontCornerMiter,
  frontCornerNotch,
  backCornerMiter,
}: CabinetAlongPathOptions) {
  const extrusions: Extrusion[] = [];
  const countertopExtrusions: Extrusion[] = [];
  let planLoops: PolyLine2[] = [];
  let footprintLoops: PolyLine2[] = [];

  const paramFromEnd = isEdgeHostedEntityFromEnd({ paramAnchor });

  const extrusionOpts: Omit<ExtrudeAlongPathOptions, "baseHeight" | "thickness" | "depth"> = {
    stripPath,
    offset: -lateralOffset,
    paramStart,
    paramStartNormalized,
    paramEnd,
    paramEndNormalized,
    reverse: paramFromEnd,
    reverseExtends: mirror,
    isDeprecatedPlacement,
    // Shared by all three layers, which are all built to the cabinet's own depth: the recess and the
    // overhang come from `frontOffset` below, not from cutting each layer to its own box.
    frontCornerMiter,
    frontCornerNotch,
    backCornerMiter,
  };

  // toe kick
  if (toeKickHeight > 0) {
    // Built to the cabinet's own depth and set back across its whole front, so the recess runs on
    // through a shaped cabinet's cut rather than stepping in and out of it.
    const { extrusion, loops } = extrudeRectAlongPathExtended({
      ...extrusionOpts,
      thickness: toeKickHeight,
      baseHeight: finalBaseHeight,
      depth: paramFromEnd ? -depth : depth,
      frontOffset: toeKickDepth,
    });

    footprintLoops = loops;
    extrusions.push(extrusion);
  }

  // cabinet base
  const cabinetThickness = thickness - toeKickHeight - countertopThickness;
  const cabinetBaseHeight = finalBaseHeight + toeKickHeight;
  const cabinetDepth = depth;

  const {
    extrusion: extrusionCabinet,
    innerPath,
    doorPath,
    path,
    loops: cabinetLoop,
  } = extrudeRectAlongPathExtended({
    ...extrusionOpts,
    thickness: cabinetThickness,
    baseHeight: cabinetBaseHeight,
    depth: paramFromEnd ? -cabinetDepth : cabinetDepth,
  });

  extrusions.push(extrusionCabinet);
  planLoops = cabinetLoop;

  // cabinet door parameters
  const cabinetDoorBasePlane = planeNewXY(0, 0, cabinetBaseHeight + HALF_CABINET_GAP);
  const cabinetDoorHeight = cabinetThickness - 2 * HALF_CABINET_GAP;

  // countertop
  if (countertopThickness > 0) {
    const coutertopBaseHeight = finalBaseHeight + thickness - countertopThickness;

    // The overhang runs the whole front the way the toe kick's recess does, so the two layers turn
    // a shaped cabinet's cut together.
    const { extrusion: extrusionCountertop, loops: countertopLoop } = extrudeRectAlongPathExtended({
      ...extrusionOpts,
      thickness: countertopThickness,
      baseHeight: coutertopBaseHeight,
      depth: paramFromEnd ? -depth : depth,
      frontOffset: -countertopOverhang,
    });

    countertopExtrusions.push(extrusionCountertop);
    planLoops = countertopLoop;
  }

  return {
    extrusions,
    countertopExtrusions,
    cabinetDoorBasePlane,
    cabinetDoorHeight,
    planLoops,
    footprintLoops,
    path,
    innerPath,
    doorPath,
  };
}

interface MakeCabinetDoorsOptions {
  path: PolyLine2;
  /** The stretch of the front doors hang on — not always the whole of it, see `ShapedFront`. */
  doorPath: PolyLine2;
  basePlane: Plane;
  height: number;
  cabinetDoorsOnLeftPath?: boolean;
  cabinetDoorsOnRightPath?: boolean;
  extrusionOutput: Extrusion[];
  paramFromEnd: boolean;
  doorStyle?: CaseworkDoorStyle;
  includeHardware?: boolean;
  hardwareVerticalPositionParam?: number;
}

export function makeCabinetDoors({
  path,
  doorPath,
  basePlane,
  height,
  cabinetDoorsOnLeftPath,
  cabinetDoorsOnRightPath,
  extrusionOutput,
  paramFromEnd,
  doorStyle = CaseworkDoorStyle.SLAB,
  includeHardware = false,
  hardwareVerticalPositionParam = 0.85,
}: MakeCabinetDoorsOptions) {
  const hardwareBasePlane = planeNewXY(0, 0, basePlane.originZ + height * hardwareVerticalPositionParam);

  if (cabinetDoorsOnLeftPath) {
    cabinetDoorsAlongPath(
      doorPath,
      basePlane,
      height,
      extrusionOutput,
      paramFromEnd,
      doorStyle,
      includeHardware,
      hardwareBasePlane,
    );
  }

  if (cabinetDoorsOnRightPath) {
    cabinetDoorsAlongPath(
      polyLine2ReverseCopy(path),
      basePlane,
      height,
      extrusionOutput,
      paramFromEnd,
      doorStyle,
      includeHardware,
      hardwareBasePlane,
    );
  }
}

function cabinetDoorsAlongPath(
  path: PolyLine2,
  cabinetDoorBasePlane: Plane,
  cabinetDoorHeight: number,
  extrusionOutput: Extrusion[],
  reverse: boolean,
  doorStyle: CaseworkDoorStyle,
  includeHardware?: boolean,
  hardwareBasePlane?: Plane,
) {
  // Splitting a run leaves a duplicate vertex at the cut, whose zero-length edge normalizes to
  // NaN. The body path is merged in extrudeRectAlongPathExtended; the door path needs it too.
  let pathPl = polyLine2MergeCoincidentVertices(polyLine2Copy(path));
  if (reverse) {
    pathPl = polyLine2ReverseCopy(pathPl);
  }

  for (let i = 0, l = polyLine2EdgeCount(pathPl); i < l; i++) {
    // get polyline edge length
    const len = polyLine2EdgeLength(pathPl, i);

    // get evenly spaced params along the length of the edge
    const params = rangeInclusiveByMinStep(0, len, CABINET_SPACING);

    let doorHardwarePath: PolyLine2;
    let [ax, ay] = polyLine2EdgeEval(pathPl, i, 0, false);

    for (let j = 1, lj = params.length; j < lj; j++) {
      const t = params[j];
      const [bx, by] = polyLine2EdgeEval(pathPl, i, t, false);
      const subLen = t - params[j - 1];

      // The door is inset by HALF_CABINET_GAP at each end, so a segment of exactly twice that
      // collapses both ends onto one point; normalizing that zero-length line yields NaN.
      if (Math.abs(subLen - 2 * HALF_CABINET_GAP) < DIST_EPSILON) {
        [ax, ay] = [bx, by];
        continue;
      }

      const [aax, aay] = line2Eval(ax, ay, bx, by, HALF_CABINET_GAP, false);
      const [bbx, bby] = line2Eval(ax, ay, bx, by, subLen - HALF_CABINET_GAP, false);

      if (doorStyle === CaseworkDoorStyle.SHAKER) {
        //back panel
        const profile = polyLine2Thicken(polyLine2ByTwoPoints(aax, aay, bbx, bby), -CABINET_DOOR_THICKNESS * 0.25);
        const door = extrudePolyLine2AlongNormal(profile, cabinetDoorBasePlane, cabinetDoorHeight);
        extrusionOutput.push(door);

        //slat pts
        const cx = profile[4];
        const cy = profile[5];
        const dx = profile[6];
        const dy = profile[7];
        doorHardwarePath = polyLine2ByTwoPoints(cx, cy, dx, dy);

        const [ccx, ccy] = line2Eval(cx, cy, dx, dy, CABINET_SHAKER_SLAT_WIDTH, false);
        const [ddx, ddy] = line2Eval(dx, dy, cx, cy, CABINET_SHAKER_SLAT_WIDTH, false);

        //vertical slats
        const slatProfileR = polyLine2Thicken(polyLine2ByTwoPoints(cx, cy, ccx, ccy), -CABINET_DOOR_THICKNESS * 0.75);
        const slatR = extrudePolyLine2AlongNormal(slatProfileR, cabinetDoorBasePlane, cabinetDoorHeight);
        extrusionOutput.push(slatR);

        const slatProfileL = polyLine2Thicken(polyLine2ByTwoPoints(ddx, ddy, dx, dy), -CABINET_DOOR_THICKNESS * 0.75);
        const slatL = extrudePolyLine2AlongNormal(slatProfileL, cabinetDoorBasePlane, cabinetDoorHeight);
        extrusionOutput.push(slatL);

        //horizontal slats
        const slatProfileB = polyLine2Thicken(polyLine2ByTwoPoints(ccx, ccy, ddx, ddy), -CABINET_DOOR_THICKNESS * 0.75);
        const slatB = extrudePolyLine2AlongNormal(slatProfileB, cabinetDoorBasePlane, CABINET_SHAKER_SLAT_WIDTH);
        extrusionOutput.push(slatB);

        const doorTopPlane = planeNewXY(0, 0, cabinetDoorBasePlane.originZ + cabinetDoorHeight);
        const slatT = extrudePolyLine2AlongNormal(slatProfileB, doorTopPlane, -CABINET_SHAKER_SLAT_WIDTH);
        extrusionOutput.push(slatT);
      } else {
        //creates slab door by default
        const profile = polyLine2Thicken(polyLine2ByTwoPoints(aax, aay, bbx, bby), -CABINET_DOOR_THICKNESS);

        const door = extrudePolyLine2AlongNormal(profile, cabinetDoorBasePlane, cabinetDoorHeight);
        extrusionOutput.push(door);

        doorHardwarePath = polyLine2ByTwoPoints(profile[4], profile[5], profile[6], profile[7]);
      }

      //hardware
      if (includeHardware === true && hardwareBasePlane) {
        const doorLength = polyLine2Length(doorHardwarePath);
        const hardwarePlacementParam = j % 2 === 0 ? 1.5 : doorLength - 1.5;
        addCaseworkHardware({ doorPath: doorHardwarePath, hardwarePlacementParam, hardwareBasePlane, extrusionOutput });
      }
      [ax, ay] = [bx, by];
    }
  }
}

interface AddCaseworkHardwareOptions {
  doorPath: PolyLine2;
  hardwareBasePlane: Plane;
  hardwarePlacementParam: number;
  extrusionOutput: Extrusion[];
}

function addCaseworkHardware({
  doorPath,
  hardwareBasePlane,
  hardwarePlacementParam,
  extrusionOutput,
}: AddCaseworkHardwareOptions) {
  for (let i = 0, l = polyLine2EdgeCount(doorPath); i < l; i++) {
    // door hardware
    const [ax, ay] = polyLine2EdgeEval(doorPath, i, hardwarePlacementParam, false);
    const [bx, by] = polyLine2EdgeEval(doorPath, i, 0, true);
    const [handleNormX, handleNormY] = vec2Normalize(ax - bx, ay - by);
    const [handlePerpX, handlePerpY] = vec2Perp(handleNormX, handleNormY);

    const [p1x, p1y] = movePtAlongNormal(ax, ay, handleNormX, handleNormY, HANDLE_THICKESS);
    const [p2x, p2y] = movePtAlongNormal(ax, ay, handleNormX, handleNormY, -HANDLE_THICKESS);

    const profile1 = polyLine2Thicken(polyLine2ByTwoPoints(p1x, p1y, p2x, p2y), 1.5);
    const handleB = extrudePolyLine2AlongNormal(profile1, hardwareBasePlane, HANDLE_THICKESS);
    const handleTopPlane = planeNewXY(0, 0, hardwareBasePlane.originZ + HANDLE_HALF_WIDTH * 2);
    const handleT = extrudePolyLine2AlongNormal(profile1, handleTopPlane, -HANDLE_THICKESS);

    const handleLine = polyLine2Move(polyLine2ByTwoPoints(p1x, p1y, p2x, p2y), -handlePerpX * 1.5, -handlePerpY * 1.5);
    const profile3 = polyLine2Thicken(handleLine, HANDLE_THICKESS);
    const handleC = extrudePolyLine2AlongNormal(profile3, hardwareBasePlane, HANDLE_HALF_WIDTH * 2);

    extrusionOutput.push(handleB);
    extrusionOutput.push(handleT);
    extrusionOutput.push(handleC);
  }
}

interface FillerPanelOpts {
  cabinetDoorsOnLeftPath?: boolean;
  cabinetDoorsOnRightPath?: boolean;
  blindPanelSide?: BlindPanelSide;
  path: PolyLine2;
  innerPath: PolyLine2;
  paramFromEnd: boolean;
  accessibleCabinetWidth: number;
  adjacentCabinetFillerWidth?: number;
  finalBaseHeight: number;
  thickness: number;
  extrusions: Extrusion[];
}
export function addFillerPanelAlongPath({
  cabinetDoorsOnLeftPath,
  cabinetDoorsOnRightPath,
  blindPanelSide,
  path,
  innerPath,
  paramFromEnd,
  accessibleCabinetWidth,
  adjacentCabinetFillerWidth,
  finalBaseHeight,
  thickness,
  extrusions,
}: FillerPanelOpts) {
  const blindPanelAtPathStart = cornerFeatureAtPathStart(blindPanelSide, paramFromEnd);
  const blindPanelLength1 = blindPanelAtPathStart ? -accessibleCabinetWidth : 0;
  const blindPanelLength2 = blindPanelAtPathStart ? 0 : -accessibleCabinetWidth;

  const fillerPath = polyLine2Extend(polyLine2Copy(path), blindPanelLength1, blindPanelLength2);
  const fillerInnerPath = polyLine2Extend(polyLine2Copy(innerPath), blindPanelLength2, blindPanelLength1);

  if (cabinetDoorsOnLeftPath) {
    const profile = adjacentCabinetFillerWidth
      ? polyLine2Thicken(fillerInnerPath, paramFromEnd ? adjacentCabinetFillerWidth : -adjacentCabinetFillerWidth)
      : fillerInnerPath;
    const filler = extrudePolyLine2AlongNormal(profile, planeNewXY(0, 0, finalBaseHeight), thickness);
    extrusions.push(filler);
  }
  if (cabinetDoorsOnRightPath) {
    const profile = adjacentCabinetFillerWidth
      ? polyLine2Thicken(fillerPath, paramFromEnd ? -adjacentCabinetFillerWidth : adjacentCabinetFillerWidth)
      : fillerPath;
    const filler = extrudePolyLine2AlongNormal(profile, planeNewXY(0, 0, finalBaseHeight), thickness);
    extrusions.push(filler);
  }
}

function movePtAlongNormal(pX: number, pY: number, nX: number, nY: number, dist: number) {
  const x = pX + nX * dist;
  const y = pY + nY * dist;

  return [x, y];
}

interface ExtrudeAlongPathOptions {
  stripPath: PolyLine2;
  baseHeight: number;
  thickness: number;
  depth: number;
  offset?: number;
  paramStart?: number;
  paramStartNormalized?: boolean;
  paramEnd?: number;
  paramEndNormalized?: boolean;
  extendEnd?: number;
  extendStart?: number;
  reverse?: boolean;
  reverseExtends?: boolean;
  isDeprecatedPlacement?: boolean;
  /** Cuts one front corner off the extruded profile at 45° — see the `ANGLED` cabinet shape. */
  frontCornerMiter?: FrontCornerMiter;
  /** Notches one front corner off the extruded profile at a right angle — see the `L_SHAPED` cabinet shape. */
  frontCornerNotch?: FrontCornerNotch;
  /** Slices one back corner off the extruded profile at 45° — see the `ANGLED` shape's `rearWidth`. */
  backCornerMiter?: BackCornerMiter;
  /** Moves the whole front: back for a toe kick's recess, forward for a countertop's overhang. */
  frontOffset?: number;
}

/**
 * `depth` with a front offset folded into it: the front moves along the depth direction, so a setback on
 * an unshaped front is simply less depth, and an overhang is more. `depth` is negative for a run
 * anchored from the far end, which flips which way the offset takes it.
 */
function depthLessFrontOffset(depth: number, frontOffset: number): number {
  return depth < 0 ? depth + frontOffset : depth - frontOffset;
}

export function extrudeRectAlongPath(opts: ExtrudeAlongPathOptions): Extrusion {
  const { extrusion } = extrudeRectAlongPathExtended(opts);
  return extrusion;
}

export function extrudeRectAlongPathExtended({
  stripPath,
  baseHeight,
  thickness,
  depth: requestedDepth,
  reverse = false,
  reverseExtends = false,
  offset = 0,
  paramStart = 0,
  paramStartNormalized = true,
  paramEnd = 1,
  paramEndNormalized = true,
  extendStart = 0,
  extendEnd = 0,
  isDeprecatedPlacement = false,
  frontCornerMiter,
  frontCornerNotch,
  backCornerMiter,
  frontOffset = 0,
}: ExtrudeAlongPathOptions) {
  // A front with no cut in it is a straight offset of the host path, so moving that front is the same
  // solid as building the whole layer shallower — and building it the second way keeps the vertex order
  // the construction had before shaped fronts existed, which every baseline on disk was generated from.
  // Only a shaped box needs the real thing: a cut front the offset has to follow around, or a back
  // slice, which is cut against `frontPath` and so would land differently if the depth moved under it.
  const foldsIntoDepth = frontOffset !== 0 && !frontCornerMiter && !frontCornerNotch && !backCornerMiter;
  const depth = foldsIntoDepth ? depthLessFrontOffset(requestedDepth, frontOffset) : requestedDepth;

  let path = polyLine2Copy(stripPath);
  path = polyLine2MergeCoincidentVertices(path);

  if (offset !== 0) {
    path = offsetPolyLine2(path, offset);
  }

  if (reverse) {
    path = polyLine2Reverse(path);
  }

  const startEdgeIndex = !reverseExtends ? 0 : polyLine2EdgeCount(path) - 1;
  if (paramStartNormalized) {
    paramStart = polyLine2EdgeLength(path, startEdgeIndex) * paramStart;
  }

  const endEdgeIndex = reverseExtends ? 0 : polyLine2EdgeCount(path) - 1;
  if (paramEndNormalized) {
    paramEnd = polyLine2EdgeLength(path, endEdgeIndex) * paramEnd;
  }

  if (isDeprecatedPlacement) {
    extendStart += -paramStart;
    if (paramEnd > 0) {
      extendEnd += paramEnd - polyLine2EdgeLength(path, endEdgeIndex);
    } else {
      extendEnd += paramEnd;
    }
    polyLine2ExtendStart(path, reverseExtends ? extendEnd : extendStart);
    polyLine2ExtendEnd(path, reverseExtends ? extendStart : extendEnd);
  } else {
    paramStart -= extendStart;
    paramEnd += extendEnd;
    // non-deprecated casework placements should only be using a single host edge, but this will work for multi-edge polylines
    const startEdgeLen = polyLine2EdgeLength(path, startEdgeIndex);
    const endEdgeLen = polyLine2EdgeLength(path, endEdgeIndex);

    const [sx, sy] = polyLine2EdgeEval(path, startEdgeIndex, reverseExtends ? startEdgeLen - paramStart : paramStart);
    const [ex, ey] = polyLine2EdgeEval(path, endEdgeIndex, reverseExtends ? endEdgeLen - paramEnd : paramEnd);

    path[0] = reverseExtends ? ex : sx;
    path[1] = reverseExtends ? ey : sy;
    path[path.length - 2] = reverseExtends ? sx : ex;
    path[path.length - 1] = reverseExtends ? sy : ey;
  }

  const closetShelfRodPath = offsetPolyLine2(path, -depth - (reverse ? 2 : -2));
  const frontPath = offsetPolyLine2(path, -depth);
  // Cutting the front path is the whole of the box shape: the loop below closes it back against the
  // host path, so every solid, plan loop and door path picks the cut up. The two are exclusive.
  let innerPath = frontPath;
  let doorPath = frontPath;
  if (frontCornerMiter) {
    ({ path: innerPath, doorPath } = miterFrontPath(
      frontPath,
      path,
      frontCornerMiter,
      reverse,
      ANGLED_DOOR_PATH_INSET,
    ));
  } else if (frontCornerNotch) {
    ({ path: innerPath, doorPath } = notchFrontPath(frontPath, path, frontCornerNotch, reverse));
  }

  // The back corner's slice reshapes the profile alone: `path` stays the host path the doors, filler
  // panels and tags are placed along, none of which the box's own back edge moves.
  const backProfilePath = backCornerMiter ? miterBackPath(path, frontPath, backCornerMiter, reverse) : path;

  if (frontOffset !== 0 && !foldsIntoDepth) {
    innerPath = offsetCabinetFront(innerPath, backProfilePath, frontOffset);
  }
  const loops: PolyLine2[] = [];

  if (polyLine2IsClosed(path)) {
    loops.push(polyLine2ReverseCopy(backProfilePath));
    loops.push(innerPath);
  } else {
    const loopSet = polyLine2Concat(polyLine2ReverseCopy(backProfilePath), innerPath, true);
    if (depth < 0) {
      polyLine2SetCW(loopSet);
    }
    loops.push(loopSet);
  }

  return {
    extrusion: extrudeFaceAlongNormal(faceNew(loops, planeNewXY(0, 0, baseHeight)), thickness),
    path,
    innerPath,
    doorPath,
    loops,
    closetShelfRodPath,
  };
}

export function sweepClosetShelfRod(rodLine: PolyLine2, height: number): BRep {
  const profile = polyLine2Reverse(arc2ByCenterStartAngle(0, 0, 0.75, 0, Math.PI * 2));
  return extrudeFaceAlongPolyLine3([profile], polyLine2OnPlane(planeNewXY(0, 0, height), rodLine), 0, 0, 1);
}

export function resolveCaseworkEdgeIds(casework: WallCasework): string[] {
  return isFixedCasework(casework) ? [casework.edgeId] : casework.edgeIds;
}

export function getHostEdgeLengthForDeprecatedCasework(
  casework: WallCaseworkFixedDeprecated,
  room: Room,
  building: Pick<Building, "roomIndex" | "wallMasses" | "walls" | "mirror">,
) {
  const {
    level: { id: levelId },
  } = room;
  const { wallIndex } = building.walls[levelId];
  const edgeIndex = room.edgeIds.indexOf(casework.edgeId);
  const wallStrip = wallIndex.wallStripFromRoomEdgeIndex(room, edgeIndex);
  if (!wallStrip) {
    return 0;
  }
  const edge = wallStrip.baselineMaxOffset(true);
  const [sx, sy] = polyLine2Start(edge);
  const [ex, ey] = polyLine2End(edge);
  return line2Len(sx, sy, ex, ey);
}

export function getHostEdgeLengthForCasework(
  casework: WallCaseworkFixed | WallCaseworkFixedDeprecated,
  room: Room,
  building: Pick<Building, "roomIndex" | "wallMasses" | "walls" | "mirror">,
) {
  if (isDeprecatedCasework(casework)) {
    return getHostEdgeLengthForDeprecatedCasework(casework, room, building);
  }
  return edgeHostedEntityGetLayerEdgeLength(casework, room, building);
}
